/**
 * Canonical occupancy / search signature for vacation-package flows.
 * Used to detect stale roomsAndPaxInfo / selectedRoom from a previous search.
 */

export const HR_VACATION_PACKAGE_NEW_SEARCH_EVENT = "hrVacationPackageNewSearch";

/**
 * Saved / draft / success package profile — occupancy must come from the order,
 * not from the current search form. Unsaved `/packages/profile` is excluded.
 * @param {string|null|undefined} pathname
 */
export function isPersistedVacationPackageProfilePath(pathname) {
  const p = String(pathname || "").replace(/\/+$/, "");
  return p.includes("/packages/profile/") && !p.endsWith("/packages/profile");
}

function toInt(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeKidsAges(room) {
  const raw =
    room?.kidsAges ?? room?.KidsAges ?? room?.childrenAges ?? room?.childAges;
  if (!Array.isArray(raw)) return [];
  return raw.map((age) => toInt(age, 0));
}

function firstNumber(...values) {
  for (const value of values) {
    if (value == null || value === "") continue;
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function normalizeSearchRooms(rooms) {
  const list = Array.isArray(rooms) ? rooms : [];
  return list.map((room) => {
    const childrenAges = normalizeKidsAges(room);
    const adults = firstNumber(room?.adults, room?.AdultsCount, room?.Adults);
    const infants = firstNumber(room?.infants, room?.InfantsCount);
    const kids = firstNumber(room?.kids, room?.KidsCount);
    return {
      adults: adults == null ? 0 : toInt(adults),
      kids: kids == null ? childrenAges.length : toInt(kids),
      infants: infants == null ? 0 : toInt(infants),
      childrenAges,
    };
  });
}

/**
 * Stable occupancy signature for the current search.
 * Destination / dates are optional and used only for cache identity.
 * @param {{ rooms?: Array, destination?: unknown, dates?: unknown }|null|undefined} input
 */
export function buildPackageOccupancySignature(input) {
  const rooms = normalizeSearchRooms(input?.rooms);
  return JSON.stringify({
    destination: input?.destination ?? null,
    dates: input?.dates ?? null,
    rooms,
  });
}

/**
 * Occupancy signature from checkout passenger slots (array lengths).
 * @param {unknown} roomsAndPaxInfo
 */
export function buildPaxSlotsOccupancySignature(roomsAndPaxInfo) {
  const slots = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo : [];
  return JSON.stringify({
    destination: null,
    dates: null,
    rooms: slots.map((slot) => ({
      adults: Array.isArray(slot?.adults) ? slot.adults.length : 0,
      kids: Array.isArray(slot?.kids) ? slot.kids.length : 0,
      infants: Array.isArray(slot?.infants) ? slot.infants.length : 0,
      childrenAges: Array.isArray(slot?.kids)
        ? slot.kids
            .map((kid) => kid?.kisSearchAge)
            .filter((age) => age != null && age !== "")
        : [],
    })),
  });
}

export function occupancySignaturesMatch(a, b) {
  if (!a || !b) return false;
  try {
    const left = typeof a === "string" ? JSON.parse(a) : a;
    const right = typeof b === "string" ? JSON.parse(b) : b;
    return JSON.stringify(left?.rooms ?? null) === JSON.stringify(right?.rooms ?? null);
  } catch {
    return a === b;
  }
}

/**
 * @param {object|null|undefined} selectedRoom
 */
export function buildSelectedRoomOccupancySignature(selectedRoom) {
  if (!selectedRoom || typeof selectedRoom !== "object") return null;
  const roomSet = selectedRoom.roomSet || selectedRoom.RoomSet || null;
  return JSON.stringify({
    roomSet,
    roomName: selectedRoom.roomName || selectedRoom.roomType || selectedRoom.RoomType || null,
    roomId:
      selectedRoom.roomId ??
      selectedRoom.RoomId ??
      selectedRoom["@seqNum"] ??
      null,
  });
}

/**
 * If the room has an LDS roomSet, it must belong to current search occupancy.
 * Rooms without roomSet are not treated as occupancy mismatches (room-type match may still apply).
 * @param {object|null|undefined} selectedRoom
 * @param {Array|null|undefined} roomsComposition
 * @param {(adults: number, kids: number) => string[]} getRoomsSetByCount
 */
export function selectedRoomMatchesSearchOccupancy(
  selectedRoom,
  roomsComposition,
  getRoomsSetByCount,
) {
  if (!selectedRoom) return true;
  const roomSet = selectedRoom.roomSet || selectedRoom.RoomSet;
  if (!roomSet) return true;
  if (typeof getRoomsSetByCount !== "function") return false;
  const rooms = Array.isArray(roomsComposition) ? roomsComposition : [];
  if (rooms.length === 0) return false;
  return rooms.some((room) => {
    const allowed = getRoomsSetByCount(toInt(room?.adults), toInt(room?.kids));
    return Array.isArray(allowed) && allowed.includes(roomSet);
  });
}

export function summarizeRoomsAndPaxForTrace(roomsAndPaxInfo) {
  if (!Array.isArray(roomsAndPaxInfo)) return [];
  return roomsAndPaxInfo.map((r) => ({
    adults: r?.adults?.length,
    kids: r?.kids?.length,
    infants: r?.infants?.length,
    roomSet: r?.room?.roomSet,
    roomName: r?.room?.roomName || r?.room?.roomType,
  }));
}

let lastOccupancyTraceKey = "";

/**
 * Dev-only occupancy trace. Dedupes on signature + selectedResult so effects do not flood.
 */
export function logPackageOccupancyTrace(source, payload = {}) {
  const isDev =
    (typeof import.meta !== "undefined" && import.meta.env?.DEV) ||
    (typeof process !== "undefined" && process.env?.NODE_ENV === "development");
  if (!isDev) return;

  const roomsAndPaxInfo = payload.roomsAndPaxInfo;
  const body = {
    source,
    searchId: payload.searchId ?? null,
    selectedResultId: payload.selectedResultId ?? null,
    ldsDealId: payload.ldsDealId ?? null,
    roomsComposition: payload.roomsComposition ?? null,
    roomsAndPaxInfo: Array.isArray(roomsAndPaxInfo)
      ? summarizeRoomsAndPaxForTrace(roomsAndPaxInfo)
      : roomsAndPaxInfo ?? null,
    selectedRoom: payload.selectedRoom ?? null,
    currentOccupancySignature: payload.currentOccupancySignature ?? null,
    selectedRoomOccupancySignature: payload.selectedRoomOccupancySignature ?? null,
  };

  const key = JSON.stringify({
    source,
    searchId: body.searchId,
    selectedResultId: body.selectedResultId,
    currentOccupancySignature: body.currentOccupancySignature,
    selectedRoomOccupancySignature: body.selectedRoomOccupancySignature,
  });
  if (key === lastOccupancyTraceKey) return;
  lastOccupancyTraceKey = key;
  // eslint-disable-next-line no-console
  console.info("[PACKAGE_OCCUPANCY_TRACE]", body);
}
